// Wordle EFAFC - Devine le nom du joueur en 6 essais
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { ArrowLeft, Delete, CornerDownLeft, Loader2, Trophy, RefreshCw } from 'lucide-react';
import { GAMES_CONFIG, calculatePoints, DIFFICULTY_MULTIPLIERS } from '../services/fanZoneConfig';
import { saveGameScore, hasPlayedToday, getDailyChallenge, createDailyChallenge } from '../services/fanZoneService';
import { db } from '../../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import logger from '../../../utils/logger';
import toast from 'react-hot-toast';

// Scrollbar styles
const scrollbarStyles = `
  .fanzone-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 228, 0, 0.3) transparent;
  }
  .fanzone-scrollbar::-webkit-scrollbar { width: 6px; }
  .fanzone-scrollbar::-webkit-scrollbar-track { background: transparent; }
  .fanzone-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, rgba(255, 228, 0, 0.4) 0%, rgba(189, 255, 0, 0.2) 100%);
    border-radius: 3px;
  }
`;

const KEYBOARD_ROWS = [
  ['A', 'Z', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['Q', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'M'],
  ['ENTER', 'W', 'X', 'C', 'V', 'B', 'N', 'DELETE']
];

const LetterTile = ({ letter, status, delay = 0, isRevealing = false }) => {
  const getColors = () => {
    switch (status) {
      case 'correct': return { bg: '#10B981', border: '#10B981' };
      case 'present': return { bg: '#F59E0B', border: '#F59E0B' };
      case 'absent': return { bg: '#374151', border: '#374151' };
      default: return { bg: 'transparent', border: letter ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)' };
    }
  };

  const colors = getColors();

  return (
    <motion.div
      initial={{ scale: letter ? 1.1 : 1 }}
      animate={{ 
        scale: 1,
        rotateX: isRevealing ? [0, 90, 0] : 0,
        backgroundColor: colors.bg,
        borderColor: colors.border
      }}
      transition={{ 
        duration: isRevealing ? 0.5 : 0.1,
        delay: isRevealing ? delay * 0.2 : 0
      }}
      className="w-12 h-14 md:w-14 md:h-16 flex items-center justify-center text-xl md:text-2xl font-bold text-white border-2 rounded-lg uppercase"
    >
      {letter}
    </motion.div>
  );
};

const KeyboardKey = ({ keyVal, status, onPress }) => {
  const isSpecial = keyVal === 'ENTER' || keyVal === 'DELETE';
  
  const getColors = () => {
    switch (status) {
      case 'correct': return 'bg-emerald-500 hover:bg-emerald-400';
      case 'present': return 'bg-amber-500 hover:bg-amber-400';
      case 'absent': return 'bg-gray-700 hover:bg-gray-600';
      default: return 'bg-white/10 hover:bg-white/20';
    }
  };

  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      onClick={() => onPress(keyVal)}
      className={`
        ${isSpecial ? 'px-3 md:px-4' : 'w-8 md:w-10'} 
        h-12 md:h-14 rounded-lg font-bold text-white transition-colors flex items-center justify-center
        ${getColors()}
      `}
    >
      {keyVal === 'DELETE' ? <Delete className="w-5 h-5" /> : 
       keyVal === 'ENTER' ? <CornerDownLeft className="w-5 h-5" /> : 
       keyVal}
    </motion.button>
  );
};

const WordleGame = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  
  const difficulty = searchParams.get('difficulty') || 'facile';
  const config = GAMES_CONFIG.wordle;
  
  const [loading, setLoading] = useState(true);
  const [targetWord, setTargetWord] = useState('');
  const [targetPlayer, setTargetPlayer] = useState(null);
  const [guesses, setGuesses] = useState([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [keyStatuses, setKeyStatuses] = useState({});
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [alreadyPlayed, setAlreadyPlayed] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(0);

  // Injecter styles
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.textContent = scrollbarStyles;
    document.head.appendChild(styleEl);
    document.documentElement.classList.add('fanzone-scrollbar');
    document.body.classList.add('fanzone-scrollbar');
    return () => {
      document.head.removeChild(styleEl);
      document.documentElement.classList.remove('fanzone-scrollbar');
      document.body.classList.remove('fanzone-scrollbar');
    };
  }, []);

  // Charger le défi du jour
  useEffect(() => {
    const loadGame = async () => {
      if (!currentUser) {
        navigate('/auth');
        return;
      }

      try {
        setLoading(true);
        
        // Vérifier si déjà joué (ignorer erreurs permission)
        try {
          const played = await hasPlayedToday(currentUser.uid, 'wordle', difficulty);
          if (played) {
            setAlreadyPlayed(true);
            setLoading(false);
            return;
          }
        } catch (e) {
          logger.warn('Permission Firebase ignorée pour hasPlayedToday');
        }

        // Récupérer le défi du jour (ignorer erreurs permission)
        let challenge = null;
        try {
          challenge = await getDailyChallenge(`wordle_${difficulty}`);
        } catch (e) {
          logger.warn('Permission Firebase ignorée pour getDailyChallenge');
        }
        
        if (!challenge) {
          // Charger les joueurs depuis Firebase
          const jouersSnapshot = await getDocs(collection(db, 'joueurs'));
          const joueurs = [];
          jouersSnapshot.forEach(doc => {
            const data = doc.data();
            if (data.nom) {
              joueurs.push({ id: doc.id, ...data });
            }
          });

          if (joueurs.length === 0) {
            // Fallback avec des noms de démonstration
            const demoPlayers = [
              { id: 'demo1', nom: 'DUMONT', prenom: 'Lucas' },
              { id: 'demo2', nom: 'MARTIN', prenom: 'Pierre' },
              { id: 'demo3', nom: 'BERNARD', prenom: 'Thomas' },
              { id: 'demo4', nom: 'PETIT', prenom: 'Antoine' },
              { id: 'demo5', nom: 'ROBERT', prenom: 'Maxime' },
            ];
            
            const diffConfig = config.difficulties[difficulty];
            const filtered = demoPlayers.filter(p => 
              p.nom.length >= (diffConfig.minLength || 3) && 
              p.nom.length <= (diffConfig.maxLength || 10)
            );
            
            const randomPlayer = filtered[Math.floor(Math.random() * filtered.length)] || demoPlayers[0];
            
            challenge = {
              word: randomPlayer.nom.toUpperCase(),
              playerId: randomPlayer.id,
              playerName: `${randomPlayer.prenom} ${randomPlayer.nom}`
            };
          } else {
            const diffConfig = config.difficulties[difficulty];
            const filtered = joueurs.filter(p => {
              const nom = p.nom.toUpperCase();
              return nom.length >= (diffConfig.minLength || 3) && 
                     nom.length <= (diffConfig.maxLength || 10);
            });
            
            const pool = filtered.length > 0 ? filtered : joueurs;
            const randomPlayer = pool[Math.floor(Math.random() * pool.length)];
            
            challenge = {
              word: randomPlayer.nom.toUpperCase(),
              playerId: randomPlayer.id,
              playerName: `${randomPlayer.prenom || ''} ${randomPlayer.nom}`.trim()
            };
          }
          
          // Essayer de sauvegarder, ignorer si permission refusée
          try {
            await createDailyChallenge(`wordle_${difficulty}`, challenge);
          } catch (e) {
            logger.warn('Impossible de sauvegarder le défi, mode démo actif');
          }
        }

        setTargetWord(challenge.word);
        setTargetPlayer({ name: challenge.playerName, id: challenge.playerId });
        
      } catch (error) {
        logger.error('Erreur chargement Wordle:', error);
        // Fallback mode démo
        const demoWord = ['DUMONT', 'MARTIN', 'PETIT', 'ROBERT'][Math.floor(Math.random() * 4)];
        setTargetWord(demoWord);
        setTargetPlayer({ name: demoWord, id: 'demo' });
      } finally {
        setLoading(false);
      }
    };

    loadGame();
  }, [currentUser, difficulty, navigate, config.difficulties]);

  const checkGuess = useCallback((guess) => {
    const result = [];
    const targetLetters = targetWord.split('');
    const guessLetters = guess.split('');
    const targetCounts = {};

    // Compter les lettres dans le mot cible
    targetLetters.forEach(letter => {
      targetCounts[letter] = (targetCounts[letter] || 0) + 1;
    });

    // Premier passage : trouver les lettres correctes
    guessLetters.forEach((letter, i) => {
      if (letter === targetLetters[i]) {
        result[i] = { letter, status: 'correct' };
        targetCounts[letter]--;
      } else {
        result[i] = { letter, status: 'pending' };
      }
    });

    // Deuxième passage : trouver les lettres présentes
    result.forEach((item, i) => {
      if (item.status === 'pending') {
        if (targetCounts[item.letter] > 0) {
          result[i].status = 'present';
          targetCounts[item.letter]--;
        } else {
          result[i].status = 'absent';
        }
      }
    });

    return result;
  }, [targetWord]);

  const handleKeyPress = useCallback((key) => {
    if (gameOver || revealing) return;

    if (key === 'DELETE') {
      setCurrentGuess(prev => prev.slice(0, -1));
    } else if (key === 'ENTER') {
      if (currentGuess.length !== targetWord.length) {
        toast.error(`Le mot doit faire ${targetWord.length} lettres`);
        return;
      }

      const result = checkGuess(currentGuess);
      setRevealing(true);
      
      // Update key statuses
      const newKeyStatuses = { ...keyStatuses };
      result.forEach(({ letter, status }) => {
        const currentStatus = newKeyStatuses[letter];
        if (status === 'correct') {
          newKeyStatuses[letter] = 'correct';
        } else if (status === 'present' && currentStatus !== 'correct') {
          newKeyStatuses[letter] = 'present';
        } else if (!currentStatus) {
          newKeyStatuses[letter] = status;
        }
      });
      setKeyStatuses(newKeyStatuses);

      setTimeout(() => {
        setGuesses(prev => [...prev, { word: currentGuess, result }]);
        setCurrentGuess('');
        setRevealing(false);

        const isWin = currentGuess === targetWord;
        const attempts = guesses.length + 1;

        if (isWin || attempts >= config.maxAttempts) {
          setGameOver(true);
          setWon(isWin);
          
          // Calculer et sauvegarder le score
          const points = isWin ? calculatePoints.wordle(attempts, difficulty) : 0;
          setPointsEarned(points);
          
          if (currentUser) {
            saveGameScore(currentUser.uid, 'wordle', difficulty, points, {
              attempts,
              won: isWin,
              word: targetWord
            }).catch(err => logger.error('Erreur sauvegarde score:', err));
          }
        }
      }, targetWord.length * 200 + 500);
    } else {
      if (currentGuess.length < targetWord.length) {
        setCurrentGuess(prev => prev + key);
      }
    }
  }, [currentGuess, targetWord, gameOver, revealing, guesses, checkGuess, keyStatuses, config.maxAttempts, difficulty, currentUser]);

  // Keyboard listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Backspace') {
        handleKeyPress('DELETE');
      } else if (e.key === 'Enter') {
        handleKeyPress('ENTER');
      } else if (/^[a-zA-Z]$/.test(e.key)) {
        handleKeyPress(e.key.toUpperCase());
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyPress]);

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #0a0f1a 0%, #0d1526 50%, #050a12 100%)' }}
      >
        <Loader2 className="w-8 h-8 text-[#FFE400] animate-spin" />
      </div>
    );
  }

  if (alreadyPlayed) {
    return (
      <div 
        className="min-h-screen flex flex-col items-center justify-center px-4"
        style={{ background: 'linear-gradient(135deg, #0a0f1a 0%, #0d1526 50%, #050a12 100%)' }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <RefreshCw className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Déjà joué aujourd'hui !</h2>
          <p className="text-gray-400 mb-6">Reviens demain pour un nouveau défi</p>
          <button
            onClick={() => navigate('/fanzone/dashboard')}
            className="px-6 py-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-black font-bold rounded-xl"
          >
            Retour au dashboard
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen pb-8"
      style={{ background: 'linear-gradient(135deg, #0a0f1a 0%, #0d1526 50%, #050a12 100%)' }}
    >
      <div className="max-w-lg mx-auto px-4 pt-6">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-6"
        >
          <button
            onClick={() => navigate('/fanzone/dashboard')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour</span>
          </button>
          
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 text-sm">
            <span className="text-gray-400">Difficulté:</span>
            <span className="text-[#FFE400] font-medium">
              {config.difficulties[difficulty]?.label} (x{DIFFICULTY_MULTIPLIERS[difficulty]})
            </span>
          </div>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="text-4xl mb-2">📝</div>
          <h1 className="text-2xl font-bold text-white">Wordle EFAFC</h1>
          <p className="text-gray-400 text-sm mt-1">
            Trouve le nom en {config.maxAttempts} essais • {targetWord.length} lettres
          </p>
        </motion.div>

        {/* Grid */}
        <div className="flex flex-col items-center gap-2 mb-8">
          {Array.from({ length: config.maxAttempts }).map((_, rowIndex) => {
            const guess = guesses[rowIndex];
            const isCurrentRow = rowIndex === guesses.length;
            
            return (
              <div key={rowIndex} className="flex gap-2">
                {Array.from({ length: targetWord.length }).map((_, colIndex) => {
                  let letter = '';
                  let status = '';
                  
                  if (guess) {
                    letter = guess.word[colIndex];
                    status = guess.result[colIndex].status;
                  } else if (isCurrentRow) {
                    letter = currentGuess[colIndex] || '';
                  }
                  
                  return (
                    <LetterTile
                      key={colIndex}
                      letter={letter}
                      status={status}
                      delay={colIndex}
                      isRevealing={revealing && isCurrentRow && guess}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Game Over Modal */}
        <AnimatePresence>
          {gameOver && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            >
              <motion.div
                initial={{ y: 20 }}
                animate={{ y: 0 }}
                className="bg-[#0d1526] border border-white/10 rounded-2xl p-8 max-w-sm w-full text-center"
              >
                {won ? (
                  <>
                    <div className="text-6xl mb-4">🎉</div>
                    <h2 className="text-2xl font-bold text-white mb-2">Bravo !</h2>
                    <p className="text-gray-400 mb-4">
                      Tu as trouvé <span className="text-[#FFE400] font-bold">{targetPlayer?.name}</span> en {guesses.length} essai{guesses.length > 1 ? 's' : ''} !
                    </p>
                  </>
                ) : (
                  <>
                    <div className="text-6xl mb-4">😔</div>
                    <h2 className="text-2xl font-bold text-white mb-2">Perdu...</h2>
                    <p className="text-gray-400 mb-4">
                      C'était <span className="text-[#FFE400] font-bold">{targetPlayer?.name}</span>
                    </p>
                  </>
                )}
                
                {pointsEarned > 0 && (
                  <div className="flex items-center justify-center gap-2 mb-6 text-[#BDFF00]">
                    <Trophy className="w-5 h-5" />
                    <span className="text-xl font-bold">+{pointsEarned} XP</span>
                  </div>
                )}
                
                <button
                  onClick={() => navigate('/fanzone/dashboard')}
                  className="w-full py-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-black font-bold rounded-xl"
                >
                  Retour au dashboard
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Keyboard */}
        <div className="space-y-2">
          {KEYBOARD_ROWS.map((row, rowIndex) => (
            <div key={rowIndex} className="flex justify-center gap-1.5">
              {row.map(key => (
                <KeyboardKey
                  key={key}
                  keyVal={key}
                  status={keyStatuses[key]}
                  onPress={handleKeyPress}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WordleGame;
